'use client';

/*
 * Ported from mstreetinvest@cec8be916:resources/js/components/crm-chat/cards/chart-card.tsx (PORTING.md §1.2:
 * "as is"). Edits: the chart theme is imported from ./chart-theme, and the switch's corners read --chat-radius
 * (the buttons inside it one pixel less), so each console theme owns them — Ledger's are mStreet's 2px and 1px;
 * and the switch's focus ring names its style (focus-visible:outline-solid): under Tailwind 4 mStreet's
 * outline-none left focus-visible:outline-2 drawing nothing (chat/__tests__/focus-ring.guard.spec.ts).
 * Generalised for the tenant chat (vCRO wave 4, 2026-10-03): an optional `locale` carries the words (Chart, Table,
 * Show as, the empty state, the first column's header) and the formats (dates, values, a category label as user
 * text) of a surface that speaks four languages. Absent, every word and format is the console's, exactly as ported.
 */
import { cn } from '@/lib/utils';
import { useMemo, useState, type ReactNode } from 'react';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { BarList } from './bar-list';
import {
    AREA_FILL_OPACITY,
    CHART_COLORS,
    chartAxisProps,
    chartGridProps,
    chartLineProps,
    chartTooltipProps,
} from './chart-theme';
import {
    compactAge,
    formatAxisDate,
    formatCompact,
    formatCount,
    formatLongDate,
    humanize,
    isIsoDate,
} from './format';
import { CardPanel } from './row-list';

type ChartValue = string | number | null | undefined;
type ChartRow = Record<string, ChartValue>;

interface ChartSeries {
    key: string;
    label: string;
}

/**
 * The words and formats of a surface outside the console (the tenant chat: English, Arabic, Hindi, French). Every
 * field the caller leaves out keeps the console's.
 */
export interface ChartCardLocale {
    chart: string;
    table: string;
    showAs: string;
    noData: string;
    /** The table's first column header (the console humanizes `x_key`). */
    category?: string;
    /** A date tick on the axis. */
    axisDate?: (value: string) => string;
    /** A date in the tooltip and the table. */
    longDate?: (value: string) => string;
    /** A value on the axis, at a bar's end, in the tooltip and in the table. */
    value?: (value: number) => string;
    /** A category label as shown: the tenant chat isolates user text (<UserText>). */
    renderCategory?: (label: string) => ReactNode;
}

interface Formats {
    axisDate: (value: string) => string;
    longDate: (value: string) => string;
    value: (value: number) => string;
    compact: (value: number) => string;
}

const CONSOLE_FORMATS: Formats = {
    axisDate: formatAxisDate,
    longDate: formatLongDate,
    value: formatCount,
    compact: formatCompact,
};

interface ChartCardProps {
    title: string;
    type?: 'area' | 'bar' | 'line';
    data: ChartRow[];
    /** Column holding each row's category or date label (the tool contract's `x_key`). */
    x_key?: string;
    series?: ChartSeries[];
    /** Legacy: keys to plot when `series` is absent. */
    dataKeys?: string[];
    /** Legacy: explicit colours; defaults to the chart tokens in fixed order. */
    colors?: string[];
    /** Another surface's words and formats; the console's when absent. */
    locale?: ChartCardLocale;
}

/** Categorical slots in fixed order; the fifth token is the neutral "other", so at most five series are drawn. */
const MAX_SERIES = CHART_COLORS.length;
const TIME_KEY = /^(date|day|week|month|period|time|bucket|week_start)$/i;

function toNumber(value: ChartValue): number | null {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);

        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
}

function axisLabel(value: unknown, dates: boolean, fmt: Formats = CONSOLE_FORMATS): string {
    const text = String(value ?? '');

    if (dates) {
        return fmt.axisDate(text);
    }

    return text.length > 12 ? `${text.slice(0, 11)}…` : text;
}

function tooltipLabel(value: unknown, dates: boolean, fmt: Formats = CONSOLE_FORMATS): string {
    const text = String(value ?? '');

    return dates ? fmt.longDate(text) : text;
}

function tooltipValue(value: unknown, fmt: Formats = CONSOLE_FORMATS): string {
    const number = typeof value === 'number' ? value : Number(value);

    return Number.isFinite(number) ? fmt.value(number) : String(value ?? '');
}

function tableCell(value: ChartValue, dates: boolean, fmt: Formats = CONSOLE_FORMATS): string {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    if (typeof value === 'number') {
        return fmt.value(value);
    }
    if (dates && isIsoDate(value)) {
        return /[T ]\d/.test(value)
            ? (compactAge(value) ?? value)
            : fmt.longDate(value);
    }

    return value;
}

function ViewToggle({
    view,
    onChange,
    locale,
}: {
    view: 'chart' | 'table';
    onChange: (view: 'chart' | 'table') => void;
    locale?: ChartCardLocale;
}) {
    return (
        <div
            role="group"
            aria-label={locale?.showAs ?? 'Show as'}
            className="inline-flex shrink-0 rounded-[var(--chat-radius)] border border-border p-px"
        >
            {(['chart', 'table'] as const).map((option) => (
                <button
                    key={option}
                    type="button"
                    aria-pressed={view === option}
                    onClick={() => onChange(option)}
                    className={cn(
                        'rounded-[calc(var(--chat-radius)_-_1px)] px-2 py-0.5 text-xs font-medium transition-colors outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-ring',
                        view === option
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:text-foreground',
                    )}
                >
                    {option === 'chart' ? (locale?.chart ?? 'Chart') : (locale?.table ?? 'Table')}
                </button>
            ))}
        </div>
    );
}

function Legend({
    series,
    colors,
    line,
}: {
    series: ChartSeries[];
    colors: string[];
    line: boolean;
}) {
    return (
        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 pt-2.5">
            {series.map((entry, index) => (
                <li
                    key={entry.key}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground"
                >
                    <span
                        aria-hidden="true"
                        className={cn('block', line ? 'h-0.5 w-3' : 'size-2')}
                        style={{ backgroundColor: colors[index] }}
                    />
                    {entry.label}
                </li>
            ))}
        </ul>
    );
}

/**
 * A chart from a tool's `chart` block. Dates and numeric bands plot as columns, lines or areas; named categories
 * plot as labelled horizontal bars, which read better than truncated tick labels. Every chart has a table view.
 */
export function ChartCard({
    title,
    type = 'area',
    data,
    x_key: xKey = 'date',
    series: seriesProp,
    dataKeys,
    colors,
    locale,
}: ChartCardProps) {
    const [view, setView] = useState<'chart' | 'table'>('chart');
    const fmt: Formats = {
        axisDate: locale?.axisDate ?? CONSOLE_FORMATS.axisDate,
        longDate: locale?.longDate ?? CONSOLE_FORMATS.longDate,
        value: locale?.value ?? CONSOLE_FORMATS.value,
        compact: locale?.value ?? CONSOLE_FORMATS.compact,
    };
    const category = (value: ChartValue, dates: boolean): ReactNode =>
        !dates && locale?.renderCategory && typeof value === 'string'
            ? locale.renderCategory(value)
            : tableCell(value, dates, fmt);

    const series = useMemo<ChartSeries[]>(
        () =>
            (
                seriesProp ??
                (dataKeys ?? ['value']).map((key) => ({ key, label: key }))
            ).slice(0, MAX_SERIES),
        [seriesProp, dataKeys],
    );

    const rows = useMemo(
        () =>
            data.map((row) => {
                const cleaned: Record<string, string | number | null> = {
                    [xKey]: String(row[xKey] ?? ''),
                };

                for (const entry of series) {
                    cleaned[entry.key] = toNumber(row[entry.key]);
                }

                return cleaned;
            }),
        [data, series, xKey],
    );

    const palette = series.map(
        (_, index) => colors?.[index] ?? CHART_COLORS[index],
    );
    const dates = rows.length > 0 && rows.every((row) => isIsoDate(row[xKey]));
    const timeSeries = dates || TIME_KEY.test(xKey);
    const values = rows.flatMap((row) =>
        series
            .map((entry) => row[entry.key])
            .filter((value): value is number => typeof value === 'number'),
    );
    const maxValue = Math.max(0, ...values);
    const hasDecimals = values.some((value) => !Number.isInteger(value));
    // Recharts rounds the top tick up (83 → 100), so leave room for one more character than the data needs, plus
    // the tick offset, or the first digit is clipped.
    const yAxisWidth = Math.max(
        32,
        Math.ceil((fmt.compact(maxValue).length + 1) * 7.5) + 12,
    );
    // Numeric bands ("0–9", "10–19") are ordered like time, so they stay columns (a histogram).
    const ordinal =
        rows.length > 0 && rows.every((row) => /^\d/.test(String(row[xKey])));
    // One point cannot draw a line; a column still shows it.
    const kind = rows.length < 2 ? 'bar' : type;
    // Named categories (sentiment, owners) read best as labelled horizontal bars.
    const asBarList = kind === 'bar' && !timeSeries && !ordinal;

    const table = (
        <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
                <thead>
                    <tr className="border-b border-border bg-muted/50">
                        <th
                            scope="col"
                            className="px-3 py-1.5 text-start font-medium whitespace-nowrap text-muted-foreground"
                        >
                            {locale?.category ?? humanize(xKey)}
                        </th>
                        {series.map((entry) => (
                            <th
                                key={entry.key}
                                scope="col"
                                className="px-3 py-1.5 text-end font-medium whitespace-nowrap text-muted-foreground"
                            >
                                {entry.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map((row, index) => (
                        <tr key={`${row[xKey]}-${index}`}>
                            <td className="px-3 py-1.5 text-foreground">
                                {category(row[xKey], dates)}
                            </td>
                            {series.map((entry) => (
                                <td
                                    key={entry.key}
                                    className="px-3 py-1.5 text-end whitespace-nowrap text-foreground tabular-nums"
                                >
                                    {tableCell(row[entry.key], dates, fmt)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    // A keyed array, not a fragment: Recharts 2 finds axes with its own react-is 18, which cannot see into a
    // React 19 fragment, so axes and grid inside one are silently dropped.
    const axes = [
        <CartesianGrid key="grid" {...chartGridProps} />,
        <XAxis
            key="x"
            dataKey={xKey}
            {...chartAxisProps}
            interval="preserveStartEnd"
            minTickGap={16}
            tickMargin={6}
            tickFormatter={(value) => axisLabel(value, dates, fmt)}
        />,
        <YAxis
            key="y"
            {...chartAxisProps}
            width={yAxisWidth}
            allowDecimals={hasDecimals}
            tickMargin={4}
            tickFormatter={(value) => fmt.compact(Number(value))}
        />,
        <Tooltip
            key="tooltip"
            {...chartTooltipProps}
            labelFormatter={(label) => tooltipLabel(label, dates, fmt)}
            formatter={(value) => tooltipValue(value, fmt)}
        />,
    ];

    const margin = { top: 8, right: 12, bottom: 0, left: 0 };

    let plot = null;

    if (kind === 'bar') {
        plot = (
            <BarChart
                data={rows}
                margin={margin}
                barGap={2}
                barCategoryGap="20%"
                accessibilityLayer
            >
                {axes}
                {series.map((entry, index) => (
                    <Bar
                        key={entry.key}
                        dataKey={entry.key}
                        name={entry.label}
                        fill={palette[index]}
                        maxBarSize={24}
                        isAnimationActive={false}
                    />
                ))}
            </BarChart>
        );
    } else if (kind === 'line') {
        plot = (
            <LineChart data={rows} margin={margin} accessibilityLayer>
                {axes}
                {series.map((entry, index) => (
                    <Line
                        key={entry.key}
                        type="linear"
                        dataKey={entry.key}
                        name={entry.label}
                        stroke={palette[index]}
                        {...chartLineProps}
                        isAnimationActive={false}
                    />
                ))}
            </LineChart>
        );
    } else {
        plot = (
            <AreaChart data={rows} margin={margin} accessibilityLayer>
                {axes}
                {series.map((entry, index) => (
                    <Area
                        key={entry.key}
                        type="linear"
                        dataKey={entry.key}
                        name={entry.label}
                        stroke={palette[index]}
                        strokeWidth={chartLineProps.strokeWidth}
                        fill={palette[index]}
                        fillOpacity={AREA_FILL_OPACITY}
                        activeDot={chartLineProps.activeDot}
                        isAnimationActive={false}
                    />
                ))}
            </AreaChart>
        );
    }

    return (
        <CardPanel
            title={title}
            aside={<ViewToggle view={view} onChange={setView} locale={locale} />}
        >
            {rows.length === 0 ? (
                <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                    {locale?.noData ?? 'No data for this period.'}
                </p>
            ) : view === 'table' ? (
                table
            ) : asBarList ? (
                <>
                    {series.length > 1 && (
                        <Legend series={series} colors={palette} line={false} />
                    )}
                    <BarList
                        rows={rows.map((row) => ({
                            label: String(row[xKey]),
                            values: series.map((entry) =>
                                Number(row[entry.key] ?? 0),
                            ),
                        }))}
                        colors={palette}
                        formatValue={fmt.value}
                        renderLabel={locale?.renderCategory}
                    />
                </>
            ) : (
                <>
                    {series.length > 1 && (
                        <Legend
                            series={series}
                            colors={palette}
                            line={kind === 'line'}
                        />
                    )}
                    <div className="h-56 px-1 pt-2 pb-1">
                        <ResponsiveContainer width="100%" height="100%">
                            {plot}
                        </ResponsiveContainer>
                    </div>
                </>
            )}
        </CardPanel>
    );
}
